// What Business needs from the app that hosts it (#212), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createBusinessModule
// from src/app/register-business.tsx; nothing here imports the app.
//
// The generic half (modals, the session) is the shell's host contract; what is
// left is specific to this module. Every member is typed structurally, so the
// host's real objects are checked where it registers them, not here.
import type { ComponentType, ReactNode } from 'react'
import type { Control } from 'react-hook-form'
import type { DateRangePreset } from '@vnetwork/datacentral-ui/primitives'
import type {
    HostLinks,
    HostModalApi,
    HostSession,
} from '@vnetwork/datacentral-shell/host'
import type { ExcelColumnMapping } from './types/excel'
import vi from './locales/vi.json'
import en from './locales/en.json'

/** The signed-in employee, as far as Business reads it. */
export interface BusinessEmployee {
    employee_id: number
}

/** The props Business gives the host's Excel export button. */
export interface BusinessExcelExportButtonProps {
    apiEndpoint: string
    columns: ExcelColumnMapping[]
    queryParams: Record<string, any>
    feature?: string
    variant?: 'secondary' | 'ghost'
    size?: 'sm' | 'md'
}

interface SelectOption {
    label: string
    value: any
}

/** The three entity lookups the filter bar reads (SimpleAsyncSelect/config). */
export interface BusinessEntityConfig {
    fetchOptions: (
        query: string,
        params?: Record<string, any>
    ) => Promise<SelectOption[]>
    fetchOptionsWithPage: (
        query: string,
        page: number,
        params?: Record<string, any>
    ) => Promise<{ options: any[]; hasMore?: boolean }>
    fetchByIds: (
        ids: any[],
        params?: Record<string, any>
    ) => Promise<SelectOption[]>
}

/** An option of the host's server-searched select. */
interface BusinessAsyncOption {
    label: string
    value: string | number
    [key: string]: unknown
}

/** The props Business gives the host's server-searched customer select. */
export interface BusinessAsyncSelectProps {
    control: Control<any>
    name: string
    asyncOptions: (query: string) => Promise<BusinessAsyncOption[]>
    queryKey?: unknown
    noOptionHandler?: (inputValue: string) => void
    label?: ReactNode
    placeholder?: string
    allowClear?: boolean
}

/** The props Business gives the host's employee select. */
export interface BusinessEmployeeSelectProps {
    control: Control<any>
    name: string
    multiple?: boolean
    departmentIds?: number[]
    onlyActive?: boolean
    label?: string
    placeholder?: string
}

export interface BusinessHost
    extends HostModalApi,
        HostSession<BusinessEmployee> {
    /**
     * The app's i18next instance, already initialised. The `business`
     * namespace is registered against it here. A package must not import
     * i18next itself to add bundles: that could hit an instance before the
     * app's init (#59 hazard 5).
     */
    i18n: {
        addResourceBundle: (
            lng: string,
            ns: string,
            resources: object
        ) => unknown
    }
    /**
     * Pages of OTHER modules Business links to. `customerNew` is the create
     * page the "add customer" option of the create form leads to (it used to
     * be a '/customers/new' string literal inside this module); the package
     * appends its own `?customer_name=` query. `customerDetail` has a `:id`
     * placeholder.
     */
    links: HostLinks<'account' | 'customerDetail' | 'customerNew'>
    /** The app's Excel export button (opens a column picker, downloads .xlsx). */
    ExcelExportButton: ComponentType<BusinessExcelExportButtonProps>
    /** The app's server-searched select (customer picker of the create form). */
    AsyncSelectWithServer: ComponentType<BusinessAsyncSelectProps>
    /** The app's employee select (companions of the create form). */
    EmployeeSelect: ComponentType<BusinessEmployeeSelectProps>
    /** The app's entity lookups (department, position, employee). */
    getEntityConfig: (
        entity: 'department' | 'position' | 'employee'
    ) => BusinessEntityConfig
    /** The app's standard quick date ranges (today, last 7 days…). */
    dateRangePresets: DateRangePreset[]
}

let current: BusinessHost | undefined

export function registerBusinessHost(host: BusinessHost): void {
    current = host
    host.i18n.addResourceBundle('vi', 'business', vi)
    host.i18n.addResourceBundle('en', 'business', en)
}

/** The registered host. Read it at render or call time, never at import. */
export function getBusinessHost(): BusinessHost {
    if (!current)
        throw new Error(
            '[business] no host registered. Call createBusinessModule({ ...hostBase, i18n, links, … }) at app bootstrap (src/app/register-business.tsx).'
        )
    return current
}

// The host's hooks, under the names the app's own hooks have, so call sites
// read as before. Each reads the host at call time, inside a component.
export const useModal = () => getBusinessHost().useModal()
export const useCurrentEmployee = () => getBusinessHost().useCurrentEmployee()
export const useIsAdmin = () => getBusinessHost().useIsAdmin()
